import React, { FC, useEffect, useState } from "react";

import log from "loglevel";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Element } from "react-scroll";
import { useLocalStorage } from "react-use";
import { Grid } from "@mui/material";

import IncidentMessage from "@/components/layouts/NotAuthenticated/components/IncidentMessage/IncidentMessage";
import NotAuthenticatedHeader from "@/components/layouts/NotAuthenticated/components/NotAuthenticatedHeader/NotAuthenticatedHeader";
import Footer from "@/components/organisms/Footer/Footer";
import config from "@/config";
import { sanitizeUrl } from "@/core/security";
import { authApi } from "@/shared/apis";
import { UrlManager } from "@/shared/apis/UrlManager";
import { AppRoute, LoginState, RefererParam, SearchParam, UserState } from "@/shared/domain";
import { useAuth, useNavigation, useRouterHook, useToast } from "@/shared/hooks";
import * as tagManager from "@/tagManager";

import ChooseBroker from "../ChooseBroker/ChooseBroker";
import { RegisterSteps } from "../Register/utils";
import AllYourFilesInOne from "./components/AllYourFilesInOne/AllYourFilesInOne";
import LoginAuthenticate from "./components/LoginAuthenticate/LoginAuthenticate";
import LoginBrokerInfo from "./components/LoginBrokerInfo/LoginBrokerInfo";
import LoginCheckEmail from "./components/LoginCheckEmail/LoginCheckEmail";
import WhatCanBeDoneWithMyBroker from "./components/WhatCanBeDoneWithMyBroker/WhatCanBeDoneWithMyBroker";
import { StyledLoginPage } from "./styled";

export const loginSteps = {
    CHECK_EMAIL: "check_email",
    AUTHENTICATE: "authenticated",
    CHOOSE_BROKER: "choose_broker",
};

export const Login: FC = () => {
    const {
        globalState: { officeId, lang },
        state: { unAuthorized, search: stateSearch, pathname: statePathname },
        pathname,
    } = useRouterHook<LoginState>();
    const { t } = useTranslation();
    const { prepare } = useAuth();
    const [step, setStep] = useState(loginSteps.CHECK_EMAIL);
    const [rememberEmail] = useLocalStorage<string>("rememberMe", "");
    const [email, setEmail] = useState(rememberEmail || "");
    const [userStatus, setUserStatus] = useState<MyBrokerApi.UserStatus>(null);
    const { warningToast } = useToast();
    const { navToRoot, navToRegisterUnknownState, navToRegisterState, navToInvitePushState } = useNavigation();

    useEffect(() => {
        if (pathname !== AppRoute.Root) navToRoot();

        if (unAuthorized) warningToast({ message: t("Toast.SessionExpired") });
    }, []);

    const handleEmailStatus = (status: MyBrokerApi.UserStatus, enteredEmail: string) => {
        log.info("handleEmailStatus", status, enteredEmail);
        prepare(status.state as UserState);
        setEmail(enteredEmail);
        setUserStatus(status);
        switch (status.state) {
            case UserState.ACCESS: // Go to next step
            case UserState.PENDING:
                return setStep(loginSteps.AUTHENTICATE);

            case UserState.UNKNOWN:
                return navToRegisterUnknownState(enteredEmail, officeId);

            case UserState.PULL:
                return navToRegisterState(enteredEmail, status.officeId, RegisterSteps.CONFIRMATION);

            case UserState.PUSH:
                return navToInvitePushState(enteredEmail, status.officeId, status.date);

            default:
                return null;
        }
    };

    const handleBrokers = async () => {
        log.info("handleBrokers... ");
        tagManager.pushLoginEvent("email");
        setStep(loginSteps.CHOOSE_BROKER);
    };

    const handleItsmeLogin = async () => {
        log.info("Start login with itsme");
        // Redirect to itsme, getting the URL from the backend
        const targetUrl = new URL(encodeURI(location.origin + AppRoute.Itsme));
        if (officeId) {
            targetUrl.searchParams.append(SearchParam.OfficeId, officeId);
        }
        if (lang) {
            targetUrl.searchParams.append(SearchParam.Language, lang);
        }
        if (statePathname || stateSearch) {
            if (statePathname) {
                targetUrl.searchParams.append(RefererParam.PathName, statePathname);
            }
            if (stateSearch) {
                targetUrl.searchParams.append(RefererParam.SearchParams, stateSearch);
            }
        }
        const { url } = await authApi.fItsmePre(targetUrl.href);
        window.location.href = sanitizeUrl(url);
    };

    const displayStep = () => {
        switch (step) {
            case loginSteps.CHECK_EMAIL:
                return (
                    <LoginCheckEmail
                        handleItsmeLogin={handleItsmeLogin}
                        email={email}
                        onChangeEmailStatus={handleEmailStatus}
                    />
                );
            case loginSteps.AUTHENTICATE:
                return (
                    <LoginAuthenticate
                        handleItsmeLogin={handleItsmeLogin}
                        userStatus={userStatus}
                        onAuthenticate={handleBrokers}
                    />
                );

            default:
                return null;
        }
    };

    return (
        <>
            <Helmet>
                <title>MyBroker</title>
                <body data-custom-theme={officeId} />
                <meta name="robots" content={!config.IS_PROD ? "noindex" : "all"} />
                {officeId && <link rel="stylesheet" href={UrlManager.brokerSchema(officeId)} />}
            </Helmet>
            {step === loginSteps.CHOOSE_BROKER && <ChooseBroker email={email} />}
            {step !== loginSteps.CHOOSE_BROKER && (
                <>
                    <StyledLoginPage>
                        <IncidentMessage />
                        <NotAuthenticatedHeader displayLoginNavigation />
                        <div className="main-login-section">
                            <Grid container>
                                <Grid item xs={12} sm={6} md={6}>
                                    <div className="login-principal">
                                        <div>
                                            <div className="mobile-container-helper">
                                                <h1>
                                                    {t("Common.LogoTitle")}, <br />
                                                    {t("Login.MyBrokerAppDesc")}
                                                </h1>
                                            </div>
                                            <p className="login-secondary-text">{t("Login.SecondaryDesc")}</p>
                                            <LoginBrokerInfo />
                                        </div>
                                    </div>
                                </Grid>
                                <Grid item xs={12} sm={6} md={6} style={{ position: "relative" }}>
                                    <div className="login-container">{displayStep()}</div>
                                </Grid>
                            </Grid>
                        </div>
                        <div className="login-secondary-section">
                            <Element id="WhatCanBeDoneWithMyBroker" name="WhatCanBeDoneWithMyBroker">
                                <WhatCanBeDoneWithMyBroker />
                            </Element>
                            <Element id="AllYourFilesInOne" name="AllYourFilesInOne">
                                <AllYourFilesInOne />
                            </Element>
                        </div>
                    </StyledLoginPage>
                    <Footer officeId={officeId} />
                </>
            )}
        </>
    );
};

export default Login;
